(() => {
    const { mediaQueries } = Theme;
    if (!mediaQueries) return;
    const mqKeys = Object.keys(mediaQueries);
    const mqLists = {};
    Theme.mediaMatches = {};
    /**
     * Handles a media query (breakpoint) change.
     */
    const handleMqChange = () => {
        const newMatches = mqKeys.reduce((acc, media) => {
            acc[media] = !!(mqLists[media] && mqLists[media].matches);
            return acc;
        }, {});
        // Update mediaMatches values after breakpoint change.
        Object.keys(newMatches).forEach((key) => {
            Theme.mediaMatches[key] = newMatches[key];
        });
        window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
    };
    mqKeys.forEach((mq) => {
        // Create mqList object for each media query.
        mqLists[mq] = window.matchMedia(mediaQueries[mq]);
        // Get initial matches for each query.
        Theme.mediaMatches[mq] = mqLists[mq].matches;
        // Add an event listener to each query.
        try {
            mqLists[mq].addEventListener('change', handleMqChange);
        } catch (err1) {
            // Fallback for legacy browsers (Safari < 14).
            mqLists[mq].addListener(handleMqChange);
        }
    });
})();
/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
    Theme.elementUtil = {};
    /**
     * Allows for removal of elements in one line of code
     * @param {object} elem - Element to remove
     */
    Theme.elementUtil.remove = (elem) => {
        if (elem) {
            if (typeof elem.remove === 'function') {
                elem.remove();
            } else {
                elem.forEach((thisElem) => {
                    thisElem.remove();
                });
            }
        }
    };
    /**
     * Checks if the passed element is in viewport or not
     * @param {object} elem - Element to check the view of
     * @returns {boolean}
     */
    Theme.elementUtil.isInViewport = (elem) => {
        const rect = elem.getBoundingClientRect();
        return (Math.round(rect.top) >= 0 && Math.round(rect.left) >= 0 && Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight) && Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth));
    };
})();
/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}
var headerHeight = document.querySelector('sticky-header').offsetHeight;
document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
/**
 * Adjusts the css top property of an element such that it sticks appropriately based on the scroll
 * direction. The container is assumed to be position: sticky, with top: 0 (or whatever). When
 * scrolling down, it'll stick to the bottom of the container, when scrolling up it'll stick to
 * the top of the container.
 */
if (!customElements.get('sticky-scroll-direction')) {
    class StickyScrollDirection extends HTMLElement {
        connectedCallback() {
            this.init();
        }
        init() {
            const headerIsSticky = document.querySelector('sticky-header header');
            this.headerHeight = Number.parseInt(getComputedStyle(this.parentElement).getPropertyValue('--header-height').replace('px', ''), 10);
            this.container = this.firstElementChild;
            this.currentTop = Number.parseInt(document.documentElement.scrollTop + this.getBoundingClientRect().top - this.offsetTop,10);
            this.defaultTop = headerIsSticky ? parseInt(this.headerHeight + 30, 10) : 48;
            this.scrollY = window.scrollY;
            this.minStickySize = this.dataset.minStickySize;
            this.toggleListeners();
            window.addEventListener('on:breakpoint-change', this.toggleListeners.bind(this));
        }
        /**
         * Either inits or destroys the component based on screen size
         */
        toggleListeners() {
            if ((this.minStickySize === 'lg' && Theme.mediaMatches.lg) || (this.minStickySize === 'md' && Theme.mediaMatches.md)) {
                this.addListeners();
            } else {
                this.removeListeners();
            }
        }
        /**
         * Init the scroll watching
         */
        addListeners() {
            this.scrollListener = this.scrollListener || this.handleScroll.bind(this);
            window.addEventListener('scroll', this.scrollListener);
            this.scrollListener();
            // Set the height of the parent to the total height of other elements
            if (this.parentElement.dataset.stickyHeightElems) {
                this.debouncedSetStickyHeights = this.debouncedSetStickyHeights || debounce(this.setStickyHeight.bind(this), 500);
                window.addEventListener('resize', this.debouncedSetStickyHeights);
                if ('MutationObserver' in window) {
                    this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems;
                    this.observer = new MutationObserver(this.debouncedSetStickyHeights);
                    document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
                        this.observer.observe(elem, {
                            childList: true,
                            attributes: true,
                            subtree: true
                        });
                    });
                }
                this.setStickyHeight();
            }
            // Listen for the opening of any disclosures, so their contents can be scrolled to if needed
            this.disclosures = this.querySelectorAll('details');
            if (this.disclosures) {
                this.disclosureChangeHandler = this.disclosureChangeHandler
                    || this.handleDisclosureChange.bind(this);
                this.disclosures.forEach((disclosure) => {
                    disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
                });
            }
        }
        /**
         * Handle the opening/closing of disclosures when they extend beyond the viewport
         * @param {object} evt - Event object.
         */
        handleDisclosureChange(evt) {
            if (evt.target.classList.contains('disclosure__panel')) {
                const summaryElem = evt.target.closest('.disclosure').querySelector('summary');
                if (!Theme.elementUtil.isInViewport(summaryElem)) {
                    window.scrollTo({
                        behavior: 'smooth',
                        top: summaryElem.getBoundingClientRect().top
                            - document.body.getBoundingClientRect().top - this.headerHeight
                    });
                }
            }
        }
        /**
         * Destroy listeners, reset css attributes
         */
        removeListeners() {
            this.container.style.top = '';
            window.removeEventListener('scroll', this.scrollListener);
            if (this.observer) {
                this.observer.disconnect();
                window.removeEventListener('resize', this.debouncedSetStickyHeights);
            }
            if (this.disclosures) {
                this.disclosures.forEach((disclosure) => {
                    disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
                });
            }
        }
        /**
         * Calculates the heights of certain elements that the height of the sticky element depends on
         */
        setStickyHeight() {
            var totalHeight = 0;
            document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
                totalHeight += elem.getBoundingClientRect().height;
            });
            this.parentElement.style.setProperty('--sticky-height', `${parseInt(totalHeight, 10)}px`);
        }
        /**
         * Updates the current css top based on scroll direction
         */
        handleScroll() {
            const bounds = this.container.getBoundingClientRect();
            const maxTop = bounds.top + window.scrollY - this.container.offsetTop + this.defaultTop;
            const minTop = this.container.clientHeight - window.innerHeight;
            if (window.scrollY < this.scrollY) {
                this.currentTop -= window.scrollY - this.scrollY;
            } else {
                this.currentTop += this.scrollY - window.scrollY;
            }
            this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.defaultTop);
            this.currentTop = this.currentTop;
            this.scrollY = window.scrollY;
            this.container.style.top = `${this.currentTop}px`;
        }
    }
    customElements.define('sticky-scroll-direction', StickyScrollDirection);
}